/**
 * Milestones — fire-and-forget recording of user funnel milestones.
 *
 * Each milestone is written once (guarded by IS NULL check).
 * Never blocks the UI. Failures are silently logged.
 *
 * Milestones tracked:
 *   - onboarding_completed_at: user finishes the onboarding wizard
 *   - first_card_at:           user creates their first flashcard
 *   - first_review_at:         user completes their first review session
 *   - fifth_card_at:           user creates their 5th flashcard
 */

import { supabase } from '@/lib/supabase';
import { reportError } from '@/services/errorTracking';
import { trackFunnelEvent } from '@/utils/attribution';

async function getUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user?.id ?? null;
}

/**
 * Write a milestone timestamp, returning whether THIS call was the one that
 * set it (i.e. the column was still NULL). The `.is(column, null)` guard means
 * the UPDATE only touches a row the first time ever; `.select()` then tells us
 * how many rows matched, which is the single source of truth for "first time".
 * Callers that only need fire-and-forget behavior can ignore the boolean.
 */
async function setMilestone(column: string): Promise<boolean> {
  const userId = await getUserId();
  if (!userId) return false;

  const { data, error } = await supabase
    .from('user_attribution')
    .update({ [column]: new Date().toISOString() })
    .eq('user_id', userId)
    .is(column, null) // only set once
    .select('user_id');

  if (error && error.code !== 'PGRST116') {
    reportError(error.message, { operation: 'recordMilestone', extra: { column } });
    return false;
  }

  return (data?.length ?? 0) > 0;
}

/**
 * First-card milestone subscription.
 *
 * The Create page must celebrate the user's first card exactly once, ever —
 * even after they delete every card and add a new one. Local card counts
 * cannot tell a true first card from a delete-all re-add (both go 0→1), so the
 * UI subscribes here and lets `first_card_at` be the authority: recordFirstCard
 * notifies listeners with `isFirstEver = true` only when the timestamp was
 * genuinely just written.
 */
type FirstCardListener = (isFirstEver: boolean) => void;
const firstCardListeners = new Set<FirstCardListener>();

/** Subscribe to the first-card milestone. Returns an unsubscribe function. */
export function onFirstCardMilestone(listener: FirstCardListener): () => void {
  firstCardListeners.add(listener);
  return () => {
    firstCardListeners.delete(listener);
  };
}

/** Record that user completed onboarding. Call from Onboarding.tsx. */
export function recordOnboardingComplete(): void {
  setMilestone('onboarding_completed_at');
  trackFunnelEvent('onboarding_completed');
}

/**
 * localStorage guard for the first-card celebration. Only consulted when the
 * server can't answer authoritatively: the user_attribution migrations define
 * NO UPDATE policy (20260320100000_user_attribution.sql — "attribution is
 * immutable"), so under RLS the milestone UPDATE can silently match 0 rows
 * with the column still NULL. In that environment this flag keeps the
 * celebration once-ever per device. Create.tsx sets it after actually showing
 * the celebration. Remove the fallback if a scoped UPDATE policy ever ships.
 */
export const FIRST_CARD_CELEBRATED_KEY = 'vidi-first-card-celebrated';

/**
 * Resolve "is this genuinely the first card ever?" with an RLS-blocked-write
 * fallback:
 *  1. UPDATE matched a row → yes (authoritative — the timestamp was just set).
 *  2. UPDATE matched nothing → SELECT first_card_at (a SELECT policy exists):
 *     non-null → already recorded → no. Still null → the write was silently
 *     blocked; fall back to the local once-ever flag.
 * Any error → false: never celebrate on uncertainty.
 */
async function resolveFirstCardEver(updateWroteRow: boolean): Promise<boolean> {
  if (updateWroteRow) return true;
  try {
    const userId = await getUserId();
    if (!userId) return false;
    const { data, error } = await supabase
      .from('user_attribution')
      .select('first_card_at')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) return false;
    if (data?.first_card_at != null) return false; // already recorded earlier
    return localStorage.getItem(FIRST_CARD_CELEBRATED_KEY) === null;
  } catch {
    return false;
  }
}

/** Record first card creation. Call when card count goes from 0 to 1. */
export function recordFirstCard(): void {
  // Notify first-card listeners with whether first_card_at was genuinely just
  // written (false on a delete-all re-add) so the UI celebrates only once ever.
  void setMilestone('first_card_at')
    .then(resolveFirstCardEver)
    .then((isFirstEver) => {
      firstCardListeners.forEach((listener) => listener(isFirstEver));
    });
  trackFunnelEvent('first_card_created');
  // Also trigger referral conversion if this user was referred
  supabase.functions.invoke('convert-referral').catch((err) => {
    reportError(err, { operation: 'convertReferral' });
  });
}

/** Record first review session. Call after first review completes. */
export function recordFirstReview(): void {
  setMilestone('first_review_at');
  trackFunnelEvent('first_review_completed');
}

/** Record 5th card creation. Call when card count crosses 5. */
export function recordFifthCard(): void {
  setMilestone('fifth_card_at');
}

/**
 * Check card count and record milestones if thresholds are crossed.
 * Call this after every card creation with the new total count.
 */
export function checkCardMilestones(newCardCount: number): void {
  if (newCardCount === 1) recordFirstCard();
  if (newCardCount === 5) recordFifthCard();
}
